(() => {
  'use strict';

  if (window.__evashuPdpAovBound) return;
  window.__evashuPdpAovBound = true;

  const publish = (name, data = {}) => {
    const analytics = window.Shopify?.analytics;
    if (typeof analytics?.publish !== 'function') return;
    Promise.resolve(
      analytics.publish(`evashu:${name}`, {
        page_path: window.location.pathname || '/',
        ...data,
      })
    ).catch(() => {});
  };

  const parseMoney = (text) => {
    const value = String(text || '').replace(/[^0-9.,-]/g, '');
    if (!value) return 0;
    const dot = value.lastIndexOf('.');
    const comma = value.lastIndexOf(',');
    const decimal = Math.max(dot, comma);
    let normalized;
    if (decimal > -1 && value.length - decimal - 1 === 2) {
      normalized = `${value.slice(0, decimal).replace(/[.,]/g, '')}.${value.slice(decimal + 1)}`;
    } else {
      normalized = value.replace(/[.,]/g, '');
    }
    const amount = Number.parseFloat(normalized);
    return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
  };

  const currencyFor = (root) => root?.dataset.currency || window.Shopify?.currency?.active || 'USD';
  const formatMoney = (root, cents) => {
    const currency = currencyFor(root);
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return `${((Number(cents) || 0) / 100).toFixed(2)} ${currency}`;
    }
  };

  const productInfoFor = (root) => root?.closest('product-info');
  const canonicalVariantId = (root) =>
    productInfoFor(root)?.querySelector('form[data-type="add-to-cart-form"] [name="id"]')?.value
      || root?.dataset.mainVariant
      || '';

  const selectedVariantJson = (root) => {
    const script = productInfoFor(root)?.querySelector(
      'variant-selects [data-selected-variant], variant-radios [data-selected-variant], [data-selected-variant][type="application/json"]'
    );
    if (!script?.textContent) return null;
    try {
      const variant = JSON.parse(script.textContent);
      const id = canonicalVariantId(root);
      return id && String(variant?.id || '') === String(id) ? variant : null;
    } catch (_) {
      return null;
    }
  };

  const domPrice = (root) => {
    const priceBox = productInfoFor(root)?.querySelector('.product__info-container .price, .price');
    if (!priceBox) return 0;
    const onSale = priceBox.classList.contains('price--on-sale');
    const node = onSale
      ? priceBox.querySelector('.price__sale .price-item--sale')
      : priceBox.querySelector('.price__regular .price-item--regular');
    return parseMoney(node?.textContent);
  };

  const currentMainPrice = (root, eventVariant = null) => {
    const id = canonicalVariantId(root);
    if (eventVariant && String(eventVariant.id || '') === String(id) && Number(eventVariant.price) >= 0) {
      return Number(eventVariant.price);
    }
    const selected = selectedVariantJson(root);
    if (selected && Number(selected.price) >= 0) return Number(selected.price);
    return domPrice(root) || Number(root?.dataset.mainPrice) || 0;
  };

  const syncRitual = (root, eventVariant = null) => {
    if (!(root instanceof Element)) return;
    const variantId = canonicalVariantId(root);
    const mainPrice = currentMainPrice(root, eventVariant);
    if (!variantId || !(mainPrice > 0)) return;

    root.dataset.mainVariant = String(variantId);
    root.dataset.mainPrice = String(mainPrice);

    const addOnTotal = Number(root.dataset.addOnTotal) || 0;
    const bundleTotal = mainPrice + addOnTotal;
    const discountPercent = Math.max(0, Number(root.dataset.discountPercent) || 0);
    const hasDiscount = discountPercent > 0 && Boolean(root.dataset.discountCode);
    const discountAmount = hasDiscount ? Math.floor((bundleTotal * discountPercent) / 100) : 0;
    const customerTotal = Math.max(0, bundleTotal - discountAmount);

    const mainPriceNode = root.querySelector('[data-ritual-main-price]');
    const bundleValueNode = root.querySelector('[data-ritual-bundle-value]');
    const totalNode = root.querySelector('[data-ritual-total]');
    if (mainPriceNode) mainPriceNode.textContent = formatMoney(root, mainPrice);
    if (bundleValueNode) bundleValueNode.textContent = formatMoney(root, bundleTotal);
    if (totalNode) totalNode.textContent = formatMoney(root, customerTotal);

    const thresholdEnabled = root.dataset.thresholdEnabled === 'true';
    const threshold = Number(root.dataset.freeShippingThreshold) || 0;
    const pill = root.querySelector('[data-ritual-pill]');
    const shipping = root.querySelector('[data-ritual-shipping]');

    if (!thresholdEnabled || !threshold) {
      if (pill) pill.textContent = 'Intention-matched set';
      return;
    }

    const shippingBasis = hasDiscount ? customerTotal : bundleTotal;
    const gap = Math.max(0, threshold - shippingBasis);
    const unlocked = gap === 0;
    if (pill) pill.textContent = unlocked ? 'Free-shipping set' : 'Intention-matched set';
    if (shipping) {
      shipping.classList.toggle('is-unlocked', unlocked);
      shipping.textContent = unlocked
        ? `Free shipping unlocked at ${formatMoney(root, threshold)}+.`
        : `${formatMoney(root, gap)} more reaches free shipping at ${formatMoney(root, threshold)}.`;
    }
  };

  const syncAllRituals = (variant = null, scope = document) => {
    if (scope instanceof Element && scope.matches('[data-evashu-ritual]')) syncRitual(scope, variant);
    scope.querySelectorAll?.('[data-evashu-ritual]').forEach((root) => syncRitual(root, variant));
  };

  const createCartLinesUpdate = (target, ids) => {
    const { CartLinesUpdateEvent } = window.StandardEvents || {};
    if (!CartLinesUpdateEvent || !target) return null;

    const deferred = CartLinesUpdateEvent.createPromise();
    target.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: ids.map((id) => ({ merchandiseId: String(id), quantity: 1 })),
        promise: deferred.promise,
      })
    );
    return deferred;
  };

  const resolveCartLinesUpdate = async (deferred) => {
    if (!deferred) return;
    const { CartLinesUpdateEvent } = window.StandardEvents || {};
    if (!CartLinesUpdateEvent) return;

    try {
      const cart = typeof CartItems !== 'undefined'
        ? await CartItems.fetchCartData()
        : await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          }).then((response) => response.json());

      if (!cart?.currency) throw new Error('Missing currency in cart response');
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
    } catch (error) {
      deferred.reject(error);
      throw error;
    }
  };

  const dispatchCartError = (target, message, code = 'SERVICE_UNAVAILABLE') => {
    const { CartErrorEvent } = window.StandardEvents || {};
    if (!CartErrorEvent || !target) return;
    target.dispatchEvent(new CartErrorEvent({ error: message, code }));
  };

  const addBundle = async (button) => {
    const root = button.closest('[data-evashu-ritual]');
    if (!root || button.disabled) return;

    syncRitual(root);
    const status = root.querySelector('[data-ritual-status]');
    const selectedVariant = canonicalVariantId(root);
    const addOnIds = (root.dataset.addOnVariants || '').split(',').filter(Boolean);
    const ids = [selectedVariant, ...addOnIds].filter(Boolean);

    if (ids.length < 2) {
      if (status) status.textContent = 'Choose the ritual products in product metafields before using this bundle.';
      return;
    }

    const linesUpdateDeferred = createCartLinesUpdate(root, ids);
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (status) status.textContent = 'Adding your ritual...';

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: ids.map((id) => ({
            id: Number(id),
            quantity: 1,
            properties: { _bundle: 'Complete the Ritual' }
          }))
        })
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const error = new Error(payload.description || 'The bundle could not be added.');
        error.code = 'INVALID';
        throw error;
      }

      await resolveCartLinesUpdate(linesUpdateDeferred);

      const discountCode = root.dataset.discountCode;
      publish('ritual_bundle_added', {
        bundle_name: 'Complete the Ritual',
        item_count: ids.length,
        selected_variant_id: String(selectedVariant),
        variant_ids: ids.map(String),
        discount_code_present: Boolean(discountCode),
      });

      if (discountCode) {
        window.location.assign(`/discount/${encodeURIComponent(discountCode)}?redirect=/cart`);
        return;
      }

      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      if (status) status.textContent = 'Complete ritual added to your cart.';
      window.location.assign('/cart');
    } catch (error) {
      if (linesUpdateDeferred) {
        try { linesUpdateDeferred.reject(error); } catch (_) {}
      }
      dispatchCartError(root, error.message || 'The bundle could not be added.', error.code || 'SERVICE_UNAVAILABLE');
      if (status) status.textContent = error.message || 'Something went wrong. Please try again.';
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-add-ritual]');
    if (button) addBundle(button);
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const variant = event?.data?.variant || event?.variant || event?.data?.data?.variant || null;
      syncAllRituals(variant);
    });
  }

  document.addEventListener('product-info:loaded', (event) => {
    requestAnimationFrame(() => syncAllRituals(null, event.target));
  });
  document.addEventListener('shopify:section:load', (event) => {
    requestAnimationFrame(() => syncAllRituals(null, event.target));
  });
  requestAnimationFrame(() => syncAllRituals());
})();
